/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {DefaultDateFormats} from '@ion/core/src/utils/DateUtil';
import Dropdown from '../components/menus/Dropdown';

/**
 * Defines the prop definitions for DateFormatChooser,
 *
 * required :              Indicates field is required or not.
 * readOnly :              Indicates whether the dropdown is read-only mode.
 * value :                 value from parent component to control this component from out.
 * defaultValue :          Default value to display when intially renders.
 * onChange :              The callback to be called when the value changes.
 * includeNone :           The flag indicates whether to add onone option into the drop down options.
 * includeNoneLabel :      None option label
 * systemFormatAsDefault : If this flag is true, then the default value take as system date format.
 */
export interface DateFormatChooserProps {
  required?: boolean;
  readOnly?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: Function;
  includeNone?: boolean;
  includeNoneLabel?: string;
};

// the function returns the date format options...
export const getDateFormatOptions = (includeNone = false, includeNoneLabel?: string) => {
  const options = Object.values(DefaultDateFormats).map(option => ({
    label : option,
    value : option
  }));
  includeNone && options.unshift({
    label : includeNoneLabel,
    value : 'none'
  });
  return options;
};

/**
 * Defines the DateFormatChooser component. The DateFormatChooser provides a list of date format
 * display within a dropdown list and allows the user to select a date format from the list
 * which will be returned to the  client as the selected date format.
 *
 * ```typescript
 *
 *  import DateFormatChooser from '@ion/dls/src/editors/';
 *
 *  <DateFormatChooser includeNone
 *    systemFormatAsDefault
 *    includeNoneLabel={translate('common:editors.dateFormat.default')}
 *    required={formState[GroupFormFields.DateFormat]?.required}
 *    onChange={onChange}
 *    value={formState[GroupFormFields.DateFormat]?.value}
 *    defaultValue={formState[GroupFormFields.DateFormat]?.value} />
 *
 * ```
 */
export const DateFormatChooser = ({
  value,
  defaultValue,
  onChange,
  required = false,
  readOnly = false,
  includeNone = false,
  includeNoneLabel = translate('common:options.none')
}: DateFormatChooserProps) => {
  const [format, setFormat] = useState(null);
  const options             = getDateFormatOptions(includeNone, includeNoneLabel);

  useEffect(() => {
    !!value && setFormat(value);
  }, [value]);

  useEffect(() => {
    const defValue  = options.find(({value}) => defaultValue === value)?.value;
    if (defValue) {
      onChange?.(defValue);
      setFormat(defValue);
    }
  }, []);

  return (
    <Dropdown required={required}
      displaySelectedLabel
      labelAsValue={false}
      disabled={readOnly}
      value={format}
      options={options}
      placeholder={translate('common:editors.dateFormat.placeholder')}
      fieldLabel={translate('common:editors.dateFormat.label')}
      onChange={onChange ? function({target: {value}}) {
        setFormat(value);
        onChange(value);
      } : null} />
  );
};

export default DateFormatChooser;
